import {
  Alert,
  AlertTitle,
  AlertDescription,
  Button,
  InputGroup,
  InputRightElement,
  Icon,
  Text,
  Box,
  FormLabel,
  List,
  ListItem,
  Divider,
  useColorModeValue,
  Flex,
  InputAddon,
  Input
} from '@chakra-ui/react';
import { useState, useMemo, useRef, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import * as z from 'zod';

import { Form, InputField } from '@/components/Form';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon } from '@heroicons/react/solid';
import { useAuth } from '@/lib/auth';
import { usePharma } from '@/providers/PharmaProvider';
import { SearchIcon } from '@heroicons/react/outline';

const schema = z.object({
  u: z.string().min(1, 'Required'),
  p: z.string().min(1, 'Required'),
  sub_user: z.string().optional(),
});

type LoginValues = {
  u: string;
  p: string;
  sub_user?: string;
  is_hashed?: boolean;
};

type LoginFormProps = {
  onSuccess: () => void;
};

export const CRCForm = ({ onSuccess }: LoginFormProps) => {
  const { login, isLoggingIn, logout } = useAuth();
  const {
    user,
    subUsers,
    isCRC,
    isREADONLY,
    pharmaLogout,
    setSelectedSubUser
  } = usePharma();

  const [error, setError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();

  // Sort subUsers to keep SIENNA_ at the top
  const sortedSubUsers = useMemo(() => {
    const siennaSites = subUsers.filter((subUser) =>
      subUser.user_name.startsWith('SIENNA_')
    ).sort((a, b) => a.user_name.localeCompare(b.user_name));

    const otherSites = subUsers.filter((subUser) =>
      !subUser.user_name.startsWith('SIENNA_')
    ).sort((a, b) => a.user_name.localeCompare(b.user_name));

    return [...siennaSites, ...otherSites];
  }, [subUsers]);

  if (!isCRC) return <Navigate to="/" />

  return (
    <div>
      <Form<LoginValues, typeof schema>
        onSubmit={async (values) => {
          values.is_hashed = true;

          try {
            const login_data = await login(values);
            if (login_data) onSuccess();
          } catch (err: any) {
            setError(err);
          }
        }}
        className="w-full"
        schema={schema}
      >
        {({ register, formState, setValue, getValues }) => (
          <>
            {error && (
              <Alert
                status="error"
                className="text-center"
                rounded="md"
                textAlign="center"
                display="block"
                mb={4}
              >
                <AlertTitle mr={2}>Oops!</AlertTitle>
                <AlertDescription>There was a problem trying to access the Site</AlertDescription>
              </Alert>
            )}

            <InputField
              type="hidden"
              size="lg"
              error={formState.errors['u']}
              registration={register('u')}
              initialVal={user?.user_name}
            />
            <InputField
              type="hidden"
              size="lg"
              error={formState.errors['p']}
              registration={register('p')}
              initialVal={user?.p}
            />

            <Box mb={4}>
              <FormLabel fontSize="md" fontWeight="medium">Select a Site</FormLabel>

              {/* Custom dropdown toggle */}
              <InputGroup onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                <Button
                  variant="outline"
                  w="full"
                  justifyContent="space-between"
                  rightIcon={<Icon as={isDropdownOpen ? ChevronUpIcon : ChevronDownIcon} />}
                  size="lg"
                >
                  {getValues('sub_user') ?
                    sortedSubUsers.find(site => site.user_id.toString() === getValues('sub_user'))?.user_name :
                    "Select a site"}
                </Button>
              </InputGroup>

              {/* Site select dropdown */}
              {isDropdownOpen && (
                <SiteSelect
                  sortedSubUsers={sortedSubUsers}
                    onChange={(value) => {
                      setValue('sub_user', value);
                      setSelectedSubUser(sortedSubUsers.find(site => site.user_id.toString() === value) || null);
                      setIsDropdownOpen(false);
                    }}
                    setIsOpen={setIsDropdownOpen}
                />
              )}
            </Box>

            <div className="flex flex-col">
              <Button
                isLoading={isLoggingIn}
                type="submit"
                w="full"
                size="lg"
                colorScheme="blue"
                isDisabled={!getValues('sub_user')}
              >
                Continue
              </Button>
            </div>
          </>
        )}
      </Form>

      <div className="text-xs text-slate-500 mt-8 text-center">
        Currently logged in as {user?.user_name} as a CRC user {isREADONLY && 'in READONLY mode'}
      </div>

      <Button
        variant="ghost"
        size="lg"
        w="full"
        colorScheme="blue"
        onClick={async () => {
          await logout().then(() => {
            pharmaLogout();
          });
          navigate('/');
        }}
        mt={2}
      >
        Logout
      </Button>
    </div>
  );
};

const useOutsideClick = (ref, callback) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callback]);
};


const SiteSelect = ({ sortedSubUsers, onChange, setIsOpen }) => {
  const selectRef = useRef<HTMLDivElement>(null);
  useOutsideClick(selectRef, () => setIsOpen(false));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const bgColor = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('blue.100', 'blue.900');
  const selectedBg = useColorModeValue('blue.200', 'blue.800');


  const [search, setSearch] = useState('');

  // Group sites by type (SIENNA_123123 vs others) (use regex to check)
  const siennaSites = useMemo(() => {
    if (!search) return sortedSubUsers.filter(site => site.user_name.match(/^SIENNA_\d+$/));
    return sortedSubUsers.filter(site => site.user_name.match(/^SIENNA_\d+$/) && site.user_name.toLowerCase().includes(search.toLowerCase()));
  }, [sortedSubUsers, search]);
  const otherSites = useMemo(() => {
    if (!search) return sortedSubUsers.filter(site => !site.user_name.match(/^SIENNA_\d+$/));
    return sortedSubUsers.filter(site => !site.user_name.match(/^SIENNA_\d+$/) && site.user_name.toLowerCase().includes(search.toLowerCase()));
  }, [sortedSubUsers, search]);

  const handleSelect = (userId) => {
    setSelectedId(userId);
    if (onChange) {
      onChange(userId.toString());
    }
  };

  return (
    <Box position="relative" zIndex={10} mt={1}>
      <Box
        maxH="300px"
        overflowY="auto"
        borderWidth="1px"
        borderRadius="md"
        bg={bgColor}
        boxShadow="sm"
        className="custom-scrollbar absolute top-0 left-0 w-full"
        ref={selectRef}
      >
        <InputGroup borderRadius="md" border="none">
          <InputAddon p={2} bg="transparent" border="none">
            <Icon as={SearchIcon} w={4} h={4} color="gray.500" />
          </InputAddon>
          <Input pl={0} placeholder="Search" border="none" value={search} onChange={(e) => setSearch(e.target.value)} />
        </InputGroup>
        <Divider />
        <List spacing={0} py={1}>
          {siennaSites.length > 0 && (
            <>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" px={3} py={1}>
                SIENNA SITES
              </Text>
              {siennaSites.map((site) => (
                <ListItem
                  key={site.user_id}
                  px={3}
                  py={2}
                  cursor="pointer"
                  bg={selectedId === site.user_id ? selectedBg : 'transparent'}
                  _hover={{ bg: selectedId === site.user_id ? selectedBg : hoverBg }}
                  onClick={() => handleSelect(site.user_id)}
                  transition="background 0.2s"
                  borderRadius="md"
                  mx={1}
                >
                  <Flex justify="space-between" align="center">
                    <Text fontWeight={selectedId === site.user_id ? "medium" : "normal"}>
                      {site.user_name}
                    </Text>
                    {selectedId === site.user_id && (
                      <Icon as={CheckIcon} w={4} h={4} color="blue.500" />
                    )}
                    <Text
                      fontSize="xs"
                      fontWeight="normal"
                      color="gray.500"
                      textAlign="right"
                    >
                      {/* Site ID */}
                      {site.user_name.slice(-3)}
                    </Text>
                  </Flex>
                </ListItem>
              ))}
            </>
          )}

          {siennaSites.length > 0 && otherSites.length > 0 && (
            <Divider my={1} />
          )}

          {otherSites.length > 0 && (
            <>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" px={3} py={1}>
                OTHER SITES
              </Text>
              {otherSites.map((site) => (
                <ListItem
                  key={site.user_id}
                  px={3}
                  py={2}
                  cursor="pointer"
                  bg={selectedId === site.user_id ? selectedBg : 'transparent'}
                  _hover={{ bg: selectedId === site.user_id ? selectedBg : hoverBg }}
                  onClick={() => handleSelect(site.user_id)}
                  transition="background 0.2s"
                  borderRadius="md"
                  mx={1}
                >
                  <Flex justify="space-between" align="center">
                    <Text fontWeight={selectedId === site.user_id ? "medium" : "normal"}>
                      {site.user_name}
                    </Text>
                    {selectedId === site.user_id && (
                      <Icon as={CheckIcon} w={4} h={4} color="blue.500" />
                    )}
                  </Flex>
                </ListItem>
              ))}
            </>
          )}
        </List>
      </Box>
    </Box>
  );
};

export default SiteSelect;
